/**
 * Units, ported from Centerline for Mac:
 * - `QuickLengthUnit` / `QuickAreaUnit` / `QuickMeasureFormat` (QuickMeasure.swift)
 * - takeoff `LinearUnit` / `AreaUnit` (TakeoffDocument.swift)
 * Raw values are identical to the Swift enums so stored data round-trips.
 */
import { groupedNumber } from "./format.js";

export type QuickLengthUnit = "ft" | "in" | "ft-in" | "yd" | "m" | "cm" | "mm";
export type QuickAreaUnit = "SF" | "SY" | "SM" | "AC";

export const QUICK_LENGTH_UNITS: readonly QuickLengthUnit[] = ["ft", "in", "ft-in", "yd", "m", "cm", "mm"];
export const QUICK_AREA_UNITS: readonly QuickAreaUnit[] = ["SF", "SY", "SM", "AC"];

export const QUICK_LENGTH_UNIT_NAMES: Record<QuickLengthUnit, string> = {
  ft: "Feet (ft)",
  in: "Inches (in)",
  "ft-in": "Feet-inches (ft-in)",
  yd: "Yards (yd)",
  m: "Meters (m)",
  cm: "Centimeters (cm)",
  mm: "Millimeters (mm)",
};

export const QUICK_AREA_UNIT_NAMES: Record<QuickAreaUnit, string> = {
  SF: "Square feet (SF)",
  SY: "Square yards (SY)",
  SM: "Square meters (SM)",
  AC: "Acres (AC)",
};

/** How many of this unit make one foot. ft-in counts in inches. */
export function lengthPerFoot(unit: QuickLengthUnit): number {
  switch (unit) {
    case "ft": return 1;
    case "in": case "ft-in": return 12;
    case "yd": return 1.0 / 3.0;
    case "m": return 0.3048;
    case "cm": return 30.48;
    case "mm": return 304.8;
  }
}

function lengthDecimals(unit: QuickLengthUnit): number {
  switch (unit) {
    case "ft": case "yd": case "m": return 2;
    case "in": case "cm": case "ft-in": return 1;
    case "mm": return 0;
  }
}

export function lengthValueFromFeet(unit: QuickLengthUnit, feet: number): number { return feet * lengthPerFoot(unit); }
export function feetFromLengthValue(unit: QuickLengthUnit, value: number): number { return value / lengthPerFoot(unit); }

/** "123.46 ft", `12'-6 1/2"`, "37,630 mm" … */
export function formatLength(unit: QuickLengthUnit, feet: number): string {
  if (unit === "ft-in") return formatFeetInches(feet);
  return `${groupedNumber(lengthValueFromFeet(unit, feet), lengthDecimals(unit))} ${unit}`;
}

/** How many of this unit make one square foot. */
export function areaPerSquareFoot(unit: QuickAreaUnit): number {
  switch (unit) {
    case "SF": return 1;
    case "SY": return 1.0 / 9.0;
    case "SM": return 0.09290304;
    case "AC": return 1.0 / 43_560;
  }
}

function areaDecimals(unit: QuickAreaUnit): number {
  switch (unit) {
    case "SF": return 1;
    case "SY": case "SM": return 2;
    case "AC": return 3;
  }
}

export function areaValueFromSquareFeet(unit: QuickAreaUnit, sf: number): number { return sf * areaPerSquareFoot(unit); }
export function squareFeetFromAreaValue(unit: QuickAreaUnit, value: number): number { return value / areaPerSquareFoot(unit); }

export function formatArea(unit: QuickAreaUnit, squareFeet: number): string {
  return `${groupedNumber(areaValueFromSquareFeet(unit, squareFeet), areaDecimals(unit))} ${unit}`;
}

/** Feet and inches to the nearest 1/`denominator` inch: 12'-6 1/2", 0'-0 3/8", 6'-0". */
export function formatFeetInches(feet: number, denominator = 8): string {
  const negative = feet < 0;
  // Swift `.rounded()` is schoolbook rounding (half away from zero); the value is non-negative here.
  const ticks = Math.round(Math.abs(feet) * 12 * denominator);
  const perFoot = 12 * denominator;
  const wholeFeet = Math.trunc(ticks / perFoot);
  const remainder = ticks % perFoot;
  const wholeInches = Math.trunc(remainder / denominator);
  let numerator = remainder % denominator;
  let reduced = denominator;
  while (numerator > 0 && numerator % 2 === 0 && reduced % 2 === 0) { numerator /= 2; reduced /= 2; }
  let inches = `${wholeInches}`;
  if (numerator > 0) inches += ` ${numerator}/${reduced}`;
  return `${negative ? "-" : ""}${groupedNumber(wholeFeet, 0)}'-${inches}"`;
}

/** Takeoff linear unit (`LinearUnit`). */
export type LinearUnit = "LF" | "FT" | "M" | "GM";
export function linearFromFeet(unit: LinearUnit, feet: number): number {
  switch (unit) {
    case "LF": case "FT": return feet;
    case "M": return feet * 0.3048;
    case "GM": return feet / 5_280;
  }
}

/** Takeoff area unit (`AreaUnit`). */
export type AreaUnit = "SF" | "SY" | "AC";
export function areaFromSquareFeet(unit: AreaUnit, sf: number): number {
  switch (unit) {
    case "SF": return sf;
    case "SY": return sf / 9;
    case "AC": return sf / 43_560;
  }
}
